import { IUndoRedoService } from "./undo";
import { IUndoRedoElement } from "./action.js";
import { registerSingleton } from "../../instantiation/common/registry.js";
import { SyncDescriptor } from "../../instantiation/common/descriptors";
import { Emitter } from "../../../base/common/events";
import { IContextKeyService } from "../../contextkey/common/contextkey";
import { Disposable, isDisposable } from "../../../base/common/lifecycle";
import { IAnalyticsService } from "../../analytics/common/analytics";

export class UndoRedoService extends Disposable implements IUndoRedoService {

    private readonly _onDidChange = new Emitter<void>();
    public readonly onDidChange = this._onDidChange.event;

    constructor(
        @IContextKeyService private readonly contextKeyService: IContextKeyService,
        @IAnalyticsService private readonly analytics: IAnalyticsService,
    ) {
        super();

        const canUndo = this.contextKeyService.createKey<boolean>("undoRedo.hasUndo", false);
        const canRedo = this.contextKeyService.createKey<boolean>("undoRedo.hasRedo", false);

        this._register(this.onDidChange(e => {
            canUndo.set(this.canUndo);
            canRedo.set(this.canRedo);
        }));
    }

    dispose(): void {
        super.dispose();
        for (const action of this.undoStack) {
            if (isDisposable(action)) {
                action.dispose();
            }
        }
        for (const action of this.redoStack) {
            if (isDisposable(action)) {
                action.dispose();
            }
        }
    }


    readonly undoStack: IUndoRedoElement[] = [];
    readonly redoStack: IUndoRedoElement[] = [];

    maxLength: number = 100;

    private _isRedoing: boolean = false;
    private _isUndoing: boolean = false;


    push(action: IUndoRedoElement) {
        if (this._isUndoing) {
            return; // Prevent re-entrancy
        }

        // Automatically group actions within a short time frame
        if (!this.pushGroup) {
            this.pushGroup = [];
            setTimeout(() => this.resolveGroup(), this.pushDelay);
        }
        this.pushGroup.push(action);
    }


    get canUndo(): boolean {
        return this.undoStack.length > 0;
    }

    undo() {
        if (this._isUndoing) {
            return; // Prevent re-entrancy
        }
        try {
            // this.resolveGroup();
            this._isUndoing = true;
            const action = this.undoStack.pop();
            if (action) {
                this.analytics?.logEvent("undoRedo.undo", { action: action.label, code: action.code });
                action.undo();
                this.redoStack.push(action);
                this._onDidChange.fire();
            }
        }
        finally {
            this._isUndoing = false;
        }
    }

    get canRedo() {
        return this.redoStack.length > 0;
    }

    redo() {
        if (this._isRedoing) {
            return; // Prevent re-entrancy
        }
        try {
            this._isRedoing = true;
            const action = this.redoStack.pop();
            if (action) {
                this.analytics?.logEvent("undoRedo.redo", { action: action.label, code: action.code });
                action.perform();
                this.undoStack.push(action);
                this._onDidChange.fire();
            }
        }
        finally {
            this._isRedoing = false;
        }
    }

    clear() {
        for (const action of this.undoStack) {
            if (isDisposable(action)) {
                action.dispose();
            }
        }
        for (const action of this.redoStack) {
            if (isDisposable(action)) {
                action.dispose();
            }
        }
        this.undoStack.length = 0;
        this.redoStack.length = 0;
        this._onDidChange.fire();
    }



    /**
     * Changes to the undo stack within a certain amount of time are automatically grouped together.
     */
    private pushGroup: IUndoRedoElement[] | null = null;
    private readonly pushDelay: number = 100; // milliseconds

    private resolveGroup() {
        if (!this.pushGroup) {
            return;
        }

        // If we have only one action, push it directly
        if (this.pushGroup.length === 1) {
            this.internalPush(this.pushGroup[0]);
        }
        // Otherwise, create a group
        else if(this.pushGroup.length > 1) {
            const items = this.pushGroup;
            const group = new UndoRedoGroup(
                items[0].label,
                items.find(a => a.code)?.code || 'unknown',
                items,
            );
            this.internalPush(group);
        }

        this.pushGroup = null;
    }

    private internalPush(action: IUndoRedoElement) {
        this.undoStack.push(action);
        this.redoStack.length = 0; // Clear redo stack on new action
        if (this.undoStack.length > this.maxLength) {
            this.undoStack.shift(); // Remove oldest action
        }
        this._onDidChange.fire();
    }
}
registerSingleton(IUndoRedoService, new SyncDescriptor(UndoRedoService));



class UndoRedoGroup extends Disposable implements IUndoRedoElement {
    constructor(
        public readonly label: string,
        public readonly code: string,
        private readonly actions: IUndoRedoElement[]
    ) {
        super();
    }

    undo(): void {
        for (let i = this.actions.length - 1; i >= 0; i--) {
            this.actions[i].undo();
        }
    }
    perform(): void {
        for (const action of this.actions) {
            action.perform();
        }
    }
}